import React, { forwardRef, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Spinner } from '@shared/components/ui/Spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select/Select';
import { Manifest, ManifestPreviewResponse } from '../../models/Manifest';
import { useManifestPreview, useGenerateManifest, useCourierIntegrations } from '../../hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faCheckCircle, faListCheck } from '@fortawesome/pro-light-svg-icons';

export interface GenerateManifestModalRef {
  open: () => void;
  close: () => void;
}

interface PreviewStepProps {
  selectedCourierId: number | null;
  onCourierSelect: (id: number) => void;
  previewData: ManifestPreviewResponse | null;
  onPreviewLoad: (data: ManifestPreviewResponse) => void;
  onGenerateSuccess: (manifest: Manifest) => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  selectedCourierId,
  onCourierSelect,
  previewData,
  onPreviewLoad,
  onGenerateSuccess,
}) => {
  const { t } = useTranslation('orderpreparationModule');
  const previewMutation = useManifestPreview();
  const generateMutation = useGenerateManifest();
  const { data: courierIntegrations, isLoading: isLoadingCouriers } = useCourierIntegrations();

  const activeCouriers = courierIntegrations?.filter((c) => c.is_active) || [];

  const handleViewPreview = async () => {
    if (!selectedCourierId) return;
    const result = await previewMutation.mutateAsync(selectedCourierId);
    onPreviewLoad(result);
  };

  const handleGenerate = async () => {
    if (!selectedCourierId) return;
    const manifest = await generateMutation.mutateAsync(selectedCourierId);
    onGenerateSuccess(manifest);
  };

  const hasEligibleOrders = previewData && previewData.data.length > 0;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">{t('manifest.modal.previewStep.selectCourier')}</label>
        <Select
          value={selectedCourierId ? String(selectedCourierId) : ''}
          onValueChange={(value) => onCourierSelect(Number(value))}
          disabled={isLoadingCouriers}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={
                isLoadingCouriers
                  ? t('manifest.modal.previewStep.loadingCouriers')
                  : t('manifest.modal.previewStep.selectCourierPlaceholder')
              }
            />
          </SelectTrigger>
          <SelectContent>
            {activeCouriers.map((courier) => (
              <SelectItem key={courier.id} value={String(courier.id)}>
                {courier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button onClick={handleViewPreview} disabled={!selectedCourierId || previewMutation.isPending} fullWidth>
        {previewMutation.isPending && <Spinner size="sm" className="mr-2 p-0" />}
        {t('manifest.modal.previewStep.viewPreview')}
      </Button>

      {previewData && (
        <div className="mt-4 space-y-4">
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-2">{t('manifest.modal.previewStep.eligibleOrders')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('manifest.modal.previewStep.totals.orders')}: {previewData.meta.total}
            </p>
            {previewData.data.length > 0 ? (
              <div className="mt-2 max-h-48 overflow-y-auto">
                <ul className="space-y-1 text-sm">
                  {previewData.data.map((order) => (
                    <li key={order.id} className="py-2 border-b last:border-0 flex justify-between items-center">
                      <span className="font-medium">{order.ref}</span>
                      <span className="text-muted-foreground">{order.customer?.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">{t('manifest.modal.previewStep.noEligibleOrders')}</p>
            )}
          </div>

          <Button onClick={handleGenerate} disabled={!hasEligibleOrders || generateMutation.isPending} fullWidth>
            {generateMutation.isPending && <Spinner size="sm" className="mr-2 p-0" />}
            {t('manifest.modal.previewStep.generateManifest')}
          </Button>
        </div>
      )}
    </div>
  );
};

interface GenerateStepProps {
  manifest: Manifest;
  onClose: () => void;
}

const GenerateStep: React.FC<GenerateStepProps> = ({ manifest, onClose }) => {
  const { t } = useTranslation('orderpreparationModule');
  const navigate = useNavigate();

  const handleDownloadExcel = () => {
    if (manifest.excel_file_path) {
      window.open(manifest.excel_file_path, '_blank');
    }
  };

  const handleViewManifests = () => {
    onClose();
    navigate('/order-preparation/manifests');
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10">
          <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-success" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t('manifest.modal.generateStep.title')}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('manifest.modal.generateStep.manifestNumber')}: <strong>{manifest.manifest_number}</strong>
          </p>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t('manifest.modal.previewStep.totals.orders')}:</span>
          <span className="font-medium">{manifest.orders_count}</span>
        </div>
        {manifest.packages_count !== null && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('manifest.modal.previewStep.totals.packages')}:</span>
            <span className="font-medium">{manifest.packages_count}</span>
          </div>
        )}
        {manifest.weight_total !== null && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('manifest.modal.previewStep.totals.weight')}:</span>
            <span className="font-medium">{manifest.weight_total} kg</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {manifest.excel_file_path && (
          <Button onClick={handleDownloadExcel} variant="outline" fullWidth>
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
            {t('manifest.modal.generateStep.downloadExcel')}
          </Button>
        )}

        <Button onClick={handleViewManifests} fullWidth>
          <FontAwesomeIcon icon={faListCheck} className="mr-2" />
          {t('manifest.modal.generateStep.viewManifests', 'Ver Manifiestos')}
        </Button>
      </div>
    </div>
  );
};

export const GenerateManifestModal = forwardRef<GenerateManifestModalRef>((_, ref) => {
  const { t } = useTranslation('orderpreparationModule');
  const modalRef = useRef<ModalCenterRef>(null);

  const [currentStep, setCurrentStep] = useState<'step-1' | 'step-2'>('step-1');
  const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<ManifestPreviewResponse | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);

  const resetState = useCallback(() => {
    setCurrentStep('step-1');
    setSelectedCourierId(null);
    setPreviewData(null);
    setManifest(null);
  }, []);

  React.useImperativeHandle(ref, () => ({
    open: () => {
      resetState();
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  const handleGenerateSuccess = useCallback((generatedManifest: Manifest) => {
    setManifest(generatedManifest);
    setCurrentStep('step-2');
  }, []);

  const handleClose = useCallback(() => {
    modalRef.current?.closeModal();
    resetState();
  }, [resetState]);

  return (
    <ModalCenter ref={modalRef} title={t('manifest.modal.title')} width="3xl" onCloseModal={resetState}>
      {currentStep === 'step-1' ? (
        <PreviewStep
          selectedCourierId={selectedCourierId}
          onCourierSelect={setSelectedCourierId}
          previewData={previewData}
          onPreviewLoad={setPreviewData}
          onGenerateSuccess={handleGenerateSuccess}
        />
      ) : (
        manifest && <GenerateStep manifest={manifest} onClose={handleClose} />
      )}
    </ModalCenter>
  );
});

GenerateManifestModal.displayName = 'GenerateManifestModal';
